import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  Calendar,
  CalendarX,
  Clock,
  CreditCard,
  LayoutGrid,
  UserPlus,
} from 'lucide-react';
import { useState } from 'react';

import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';
import { EnrollStudentModal } from '~features/students/ui/student-payment/enroll-students';

import { Button } from '~shared/shadcn/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';

import { StudentSelectorDialog } from './student-selector-dialog';

export const QuickActionsToolbar = () => {
  const { hasSuperAdminAccess, hasAdminAccess } = useCurrentUserRole();
  const [selectStudentDialogOpen, setSelectStudentDialogOpen] = useState(false);
  const [enrollmentStudent, setEnrollmentStudent] = useState<string | null>(
    null,
  );
  const [enrollmentDialogOpen, setEnrollmentDialogOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);

  const onEnroll = (studentId: string) => {
    setEnrollmentStudent(studentId);
    setSelectStudentDialogOpen(false);
    setEnrollmentDialogOpen(true);
    setMobileActionsOpen(false);
  };

  const openEnrollStudent = () => {
    setMobileActionsOpen(false);
    setSelectStudentDialogOpen(true);
  };

  const actionButtonClass =
    'group h-11 whitespace-nowrap rounded-xl px-5 font-medium transition-all duration-150 hover:scale-[1.01] active:scale-[0.985]';

  return (
    <>
      <div className='w-full min-w-0 border-b border-border/50 bg-white/95 py-4 shadow-sm'>
        {/* Mobile / tablet: single entry point (match nav collapse at 1100px) */}
        <div className='min-[1100px]:hidden'>
          <Button
            type='button'
            variant='outline'
            className={`${actionButtonClass} h-11 w-full gap-2.5 border-border/60 bg-white hover:border-border hover:bg-[#F9FAFB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]`}
            onClick={() => {
              setMobileActionsOpen(true);
            }}
          >
            <LayoutGrid className='size-4' />
            Quick Actions
          </Button>
        </div>

        {/* Desktop: inline toolbar */}
        <div className='hidden w-full min-w-0 flex-wrap items-center gap-2 min-[1100px]:flex min-[1100px]:gap-3'>
          {hasAdminAccess && (
            <Button
              className={`${actionButtonClass} relative gap-2.5 bg-[#16A34A] font-semibold text-white shadow-sm hover:bg-[#15803D] hover:shadow-[0_6px_20px_rgba(22,163,74,0.25)]`}
              onClick={() => {
                setSelectStudentDialogOpen(true);
              }}
            >
              <UserPlus />
              Enroll Student
            </Button>
          )}
          {hasSuperAdminAccess && (
            <Button
              asChild
              variant='outline'
              className={`${actionButtonClass} border-border/60 bg-white hover:border-border hover:bg-[#F9FAFB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]`}
            >
              <Link
                className='flex items-center gap-2.5'
                to={'/payment/upcoming-payments'}
              >
                <Clock />
                <span>Upcoming Payments</span>
              </Link>
            </Button>
          )}

          <Button
            asChild
            variant='outline'
            className={`${actionButtonClass} border-border/60 bg-white hover:border-border hover:bg-[#F9FAFB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]`}
          >
            <Link className='flex items-center gap-2.5' to={'/attendance'}>
              <Calendar />
              <span>Take Attendance</span>
            </Link>
          </Button>

          {hasSuperAdminAccess && (
            <>
              <Button
                asChild
                className={`${actionButtonClass} relative gap-2.5 bg-[#FFC300] font-semibold text-[#000814] shadow-sm hover:bg-[#FFD60A] hover:shadow-[0_6px_20px_rgba(255,195,0,0.25)]`}
              >
                <Link
                  className='flex items-center gap-2.5'
                  to={'/payment/bulk-charge-students'}
                >
                  <CreditCard />
                  <span>Bulk Charge</span>
                </Link>
              </Button>

              <Button
                asChild
                variant='outline'
                className={`${actionButtonClass} border-red-200 bg-white text-red-700 hover:border-red-300 hover:bg-red-50 hover:shadow-[0_4px_12px_rgba(220,38,38,0.15)]`}
              >
                <Link
                  className='flex items-center gap-2.5'
                  to={'/payment/failed-payments'}
                >
                  <AlertTriangle />
                  <span>Failed Payments</span>
                </Link>
              </Button>
            </>
          )}

          <Button
            asChild
            variant='outline'
            className={`${actionButtonClass} border-border/60 bg-white hover:border-border hover:bg-[#F9FAFB] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]`}
          >
            <Link className='flex items-center gap-2.5' to={'/scheduled-drops'}>
              <CalendarX />
              <span>Scheduled Drops</span>
            </Link>
          </Button>
        </div>
      </div>

      <Sheet open={mobileActionsOpen} onOpenChange={setMobileActionsOpen}>
        <SheetContent side='bottom' className='rounded-t-2xl px-4 pb-8 pt-6'>
          <SheetHeader className='mb-4 text-left'>
            <SheetTitle>Quick Actions</SheetTitle>
          </SheetHeader>
          <div className='flex flex-col gap-2'>
            {hasAdminAccess && (
              <Button
                className={`${actionButtonClass} h-12 w-full justify-start gap-3 bg-[#16A34A] font-semibold text-white shadow-sm hover:bg-[#15803D]`}
                onClick={openEnrollStudent}
              >
                <UserPlus className='size-4' />
                Enroll Student
              </Button>
            )}
            {hasSuperAdminAccess && (
              <Button
                asChild
                variant='outline'
                className={`${actionButtonClass} h-12 w-full justify-start gap-3 border-border/60 bg-white`}
              >
                <Link
                  className='flex items-center gap-3'
                  to={'/payment/upcoming-payments'}
                  onClick={() => {
                    setMobileActionsOpen(false);
                  }}
                >
                  <Clock className='size-4' />
                  Upcoming Payments
                </Link>
              </Button>
            )}
            <Button
              asChild
              variant='outline'
              className={`${actionButtonClass} h-12 w-full justify-start gap-3 border-border/60 bg-white`}
            >
              <Link
                className='flex items-center gap-3'
                to={'/attendance'}
                onClick={() => {
                  setMobileActionsOpen(false);
                }}
              >
                <Calendar className='size-4' />
                Take Attendance
              </Link>
            </Button>
            {hasSuperAdminAccess && (
              <>
                <Button
                  asChild
                  className={`${actionButtonClass} h-12 w-full justify-start gap-3 bg-[#FFC300] font-semibold text-[#000814] shadow-sm hover:bg-[#FFD60A]`}
                >
                  <Link
                    className='flex items-center gap-3'
                    to={'/payment/bulk-charge-students'}
                    onClick={() => {
                      setMobileActionsOpen(false);
                    }}
                  >
                    <CreditCard className='size-4' />
                    Bulk Charge
                  </Link>
                </Button>
                <Button
                  asChild
                  variant='outline'
                  className={`${actionButtonClass} h-12 w-full justify-start gap-3 border-red-200 bg-white text-red-700 hover:bg-red-50`}
                >
                  <Link
                    className='flex items-center gap-3'
                    to={'/payment/failed-payments'}
                    onClick={() => {
                      setMobileActionsOpen(false);
                    }}
                  >
                    <AlertTriangle className='size-4' />
                    Failed Payments
                  </Link>
                </Button>
              </>
            )}
            <Button
              asChild
              variant='outline'
              className={`${actionButtonClass} h-12 w-full justify-start gap-3 border-border/60 bg-white`}
            >
              <Link
                className='flex items-center gap-3'
                to={'/scheduled-drops'}
                onClick={() => {
                  setMobileActionsOpen(false);
                }}
              >
                <CalendarX className='size-4' />
                Scheduled Drops
              </Link>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <StudentSelectorDialog
        open={selectStudentDialogOpen}
        onOpenChange={setSelectStudentDialogOpen}
        onEnroll={onEnroll}
      />
      <EnrollStudentModal
        studentId={enrollmentStudent ?? ''}
        dialogOpen={enrollmentDialogOpen}
        setDialogOpen={setEnrollmentDialogOpen}
      />
    </>
  );
};
